import { useRef, type DragEvent, type KeyboardEvent } from 'react';
import { GripHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { changeRuleField, isFieldSelectable, operatorTriggerLabel, unitSuffix, type NudgeRuleDraft } from '../nudge-rules';
import type { NudgeField } from '../types';
import { FIELD_MENU_ITEM, ROW_INPUT, ROW_SELECT } from '../ui-chrome';

interface NudgeRuleRowProps {
	row: NudgeRuleDraft;
	index: number;
	fields: NudgeField[];
	takenFieldKeys: ReadonlySet<string>;
	editable: boolean;
	isDragging: boolean;
	onChange: (row: NudgeRuleDraft) => void;
	onRemove: () => void;
	onMove: (from: number, to: number) => void;
	onDragStart: (index: number) => void;
	onDragEnd: () => void;
	onDropOn: (index: number) => void;
}

/** The value box for the chosen field: a number with its unit, a URL, a Yes/No, or nothing. */
const RuleValueInput = ({
	field,
	value,
	editable,
	onChange
}: {
	field: NudgeField | undefined;
	value: string;
	editable: boolean;
	onChange: (value: string) => void;
}) => {
	// 'none' (Exit intent and friends) has no value; the empty cell keeps the remove button aligned.
	if (!field || field.valueType === 'none') return <div className='min-w-0 flex-1' />;

	if (field.valueType === 'boolean') {
		return (
			<Select value={value} onValueChange={onChange} disabled={!editable}>
				<SelectTrigger className={cn(ROW_SELECT, 'min-w-0 flex-1')} aria-label={`${field.label} value`}>
					<SelectValue placeholder='Select' />
				</SelectTrigger>
				<SelectContent>
					{(field.options ?? []).map((option) => (
						<SelectItem key={String(option.value)} value={String(option.value)} className='text-[13px]'>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		);
	}

	if (field.valueType === 'string') {
		return (
			<Input
				value={value}
				disabled={!editable}
				onChange={(event) => onChange(event.target.value)}
				placeholder={field.placeholder ?? ''}
				maxLength={field.max ?? undefined}
				aria-label={`${field.label} value`}
				className={cn(ROW_INPUT, 'min-w-0 flex-1')}
			/>
		);
	}

	const suffix = unitSuffix(field.unit);
	return (
		<div className='relative min-w-0 flex-1'>
			<Input
				type='number'
				inputMode='numeric'
				step={1}
				min={field.min ?? undefined}
				max={field.max ?? undefined}
				value={value}
				disabled={!editable}
				onChange={(event) => onChange(event.target.value)}
				aria-label={`${field.label} value`}
				className={cn(
					ROW_INPUT,
					'[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
					suffix && 'pr-20'
				)}
			/>
			{suffix && (
				<span className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-[13px] text-gray-800'>
					{suffix}
				</span>
			)}
		</div>
	);
};

/**
 * One "When to nudge" row: drag handle, field, operator, value, remove.
 *
 * Reordering is native drag and drop on the handle, with the arrow keys on the focused handle as
 * the keyboard path. No drag library is installed, and one list of a few rows does not need one.
 */
export const NudgeRuleRow = ({
	row,
	index,
	fields,
	takenFieldKeys,
	editable,
	isDragging,
	onChange,
	onRemove,
	onMove,
	onDragStart,
	onDragEnd,
	onDropOn
}: NudgeRuleRowProps) => {
	const rowRef = useRef<HTMLDivElement>(null);
	const field = fields.find((candidate) => candidate.key === row.field);
	const operatorLabel = field?.operators.find((operator) => operator.value === row.operator)?.label;

	const handleDragStart = (event: DragEvent<HTMLButtonElement>) => {
		event.dataTransfer.effectAllowed = 'move';
		event.dataTransfer.setData('text/plain', row.id);
		if (rowRef.current) event.dataTransfer.setDragImage(rowRef.current, 12, 18);
		onDragStart(index);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			onMove(index, index - 1);
		} else if (event.key === 'ArrowDown') {
			event.preventDefault();
			onMove(index, index + 1);
		}
	};

	const selectField = (key: string) => {
		const next = fields.find((candidate) => candidate.key === key);
		if (next) onChange(changeRuleField(row, next));
	};

	return (
		<div
			ref={rowRef}
			className={cn('flex items-center gap-2', isDragging && 'opacity-50')}
			onDragOver={(event) => {
				if (editable) event.preventDefault();
			}}
			onDrop={(event) => {
				event.preventDefault();
				onDropOn(index);
			}}
		>
			<button
				type='button'
				draggable={editable}
				disabled={!editable}
				onDragStart={handleDragStart}
				onDragEnd={onDragEnd}
				onKeyDown={handleKeyDown}
				aria-label={`Reorder row ${index + 1}. Use the arrow keys to move it.`}
				className='flex size-[22px] shrink-0 cursor-grab items-center justify-center rounded text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing disabled:cursor-default'
			>
				<GripHorizontal className='size-4' />
			</button>

			<Select value={row.field} onValueChange={selectField} disabled={!editable}>
				<SelectTrigger className={cn(ROW_SELECT, 'w-[236px] shrink-0')} aria-label='Field'>
					<SelectValue placeholder='Select' />
				</SelectTrigger>
				<SelectContent className='rounded-xl p-0.5'>
					{fields.map((candidate) => (
						<SelectItem
							key={candidate.key}
							value={candidate.key}
							disabled={!isFieldSelectable(candidate, takenFieldKeys, row.field)}
							className={FIELD_MENU_ITEM}
						>
							{candidate.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			<Select
				value={row.operator}
				onValueChange={(operator) => onChange({ ...row, operator })}
				disabled={!editable || !field}
			>
				<SelectTrigger className={cn(ROW_SELECT, 'w-[120px] shrink-0')} aria-label='Operator'>
					<SelectValue placeholder='Select'>{operatorLabel ? operatorTriggerLabel(operatorLabel) : undefined}</SelectValue>
				</SelectTrigger>
				<SelectContent className='rounded-xl p-0.5'>
					{(field?.operators ?? []).map((operator) => (
						<SelectItem key={operator.value} value={operator.value} className='h-7 text-[13px] text-ink'>
							{operator.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			<RuleValueInput
				field={field}
				value={row.value}
				editable={editable}
				onChange={(value) => onChange({ ...row, value })}
			/>

			<Button
				type='button'
				variant='ghost'
				size='icon-sm'
				disabled={!editable}
				onClick={onRemove}
				aria-label={`Remove ${field?.label ?? 'row'}`}
				className='shrink-0 text-gray-800 disabled:opacity-100'
			>
				<X className='size-4' />
			</Button>
		</div>
	);
};
